/**
 * errorBus.ts
 *
 * A lightweight event bus that lets the Axios interceptor (which lives
 * outside the React tree) signal React components about API errors.
 *
 * Usage:
 *   // Emit from interceptor:
 *   errorBus.emit('unauthorized');
 *   errorBus.emit('api-error', { message: 'Something went wrong', status: 500 });
 *
 *   // Listen in a React component:
 *   useEffect(() => {
 *     const unsub = errorBus.on('unauthorized', handler);
 *     return unsub;
 *   }, []);
 */

type ApiErrorDetail = { message: string; status?: number };

type ErrorBusEvents = {
  unauthorized: void;
  'api-error': ApiErrorDetail;
};

type Listener<T> = T extends void ? () => void : (payload: T) => void;

class ErrorBus {
  private listeners = new Map<string, Set<Function>>();

  on<K extends keyof ErrorBusEvents>(event: K, listener: Listener<ErrorBusEvents[K]>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener);
    // Returns an unsubscribe function
    return () => this.listeners.get(event)?.delete(listener);
  }

  emit<K extends keyof ErrorBusEvents>(
    ...args: ErrorBusEvents[K] extends void ? [event: K] : [event: K, payload: ErrorBusEvents[K]]
  ): void {
    const [event, payload] = args as [K, ErrorBusEvents[K]];
    this.listeners.get(event)?.forEach((fn) => fn(payload));
  }
}

export const errorBus = new ErrorBus();
